'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { omit } from 'lodash'
import { SearchIcon } from 'lucide-react'

import { AccountFilterButton } from '~/app/accounting/filters/AccountFilterButton'
import {
  CostBasisFilterButton,
  CostBasisFilterValue,
} from '~/app/accounting/filters/CostBasisFilterButton'
import { DateFilterButton } from '~/app/accounting/filters/DateFilterButton'
import { NetworkFilterButton } from '~/app/accounting/filters/NetworkFilterButton'
import {
  RemainingFilterButton,
  RemainingFilterValue,
} from '~/app/accounting/filters/RemainingFilterButton'
import { TokenFilterButton } from '~/app/accounting/filters/TokenFilterButton'
import {
  UnitCostFilterButton,
  UnitCostFilterValue,
} from '~/app/accounting/filters/UnitCostFilterButton'
import { ValueFilterButton } from '~/app/accounting/filters/ValueFilterButton'
import {
  COLUMN_IDS,
  getColumnFilterValue,
  setColumnFilterValue,
} from '~/app/accounting/filters/helpers'
import { Period } from '~/app/accounting/period-utils'
import {
  useOpenLotsExport,
  useTokensExport,
  useTransactionsExport,
} from '~/app/accounting/useAccountingExports'
import { Button } from '~/components/component-library'
import { Input } from '~/components/inputs/Input'
import { useActiveOrg } from '~/hooks/org'
import { AccountingTz } from '~/utils/timezone'

type FilterParams = Record<string, string>

// A transaction hash is 0x followed by 64 hex characters.
const TRANSACTION_HASH_REGEX = /^0x[a-fA-F0-9]{64}$/

type FilterBarMode = 'transactions' | 'tokens' | 'lots'

type AccountingFilterBarProps = {
  period: Period | null
  setPeriod: (period: Period | null, overrideTz?: AccountingTz) => void
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
  filterParams: FilterParams
  tab: FilterBarMode
  tokenSearch: string
  onTokenSearchChange: (value: string) => void
  lotsSearch: string
  onLotsSearchChange: (value: string) => void
  lotsRemaining: RemainingFilterValue
  onLotsRemainingChange: (value: RemainingFilterValue) => void
  lotsCostBasis: CostBasisFilterValue
  onLotsCostBasisChange: (value: CostBasisFilterValue) => void
  lotsUnitCost: UnitCostFilterValue
  onLotsUnitCostChange: (value: UnitCostFilterValue) => void
}

export const AccountingFilterBar = ({
  period,
  setPeriod,
  columnFilters,
  setColumnFilters,
  filterParams,
  tab,
  tokenSearch,
  onTokenSearchChange,
  lotsSearch,
  onLotsSearchChange,
  lotsRemaining,
  onLotsRemainingChange,
  lotsCostBasis,
  onLotsCostBasisChange,
  lotsUnitCost,
  onLotsUnitCostChange,
}: AccountingFilterBarProps) => {
  const isTransactions = tab === 'transactions'
  const isLots = tab === 'lots'
  const memoFilterValue = getColumnFilterValue(columnFilters, COLUMN_IDS.MEMO)

  const transactionHashFilterValue = getColumnFilterValue(
    columnFilters,
    COLUMN_IDS.TRANSACTION_HASH,
  )

  // One search box covers both memos and transaction hashes: a pasted hash
  // routes to the transactionHash filter, anything else to the memo filter.
  const handleSearchChange = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      setColumnFilters((prev) => {
        const cleared = setColumnFilterValue(
          setColumnFilterValue(prev, COLUMN_IDS.MEMO, ''),
          COLUMN_IDS.TRANSACTION_HASH,
          '',
        )
        return TRANSACTION_HASH_REGEX.test(trimmed)
          ? setColumnFilterValue(cleared, COLUMN_IDS.TRANSACTION_HASH, trimmed)
          : setColumnFilterValue(cleared, COLUMN_IDS.MEMO, value)
      })
    },
    [setColumnFilters],
  )

  return (
    <div className="flex w-full items-center gap-2">
      {isTransactions && (
        <DebouncedSearch
          placeholder="Search by memo or hash..."
          value={transactionHashFilterValue || memoFilterValue}
          onChange={handleSearchChange}
        />
      )}
      {tab === 'tokens' && (
        <TokenSearch value={tokenSearch} onChange={onTokenSearchChange} />
      )}
      {isLots && (
        <DebouncedSearch
          placeholder="Search by sender address..."
          value={lotsSearch}
          onChange={onLotsSearchChange}
        />
      )}
      <DateFilterButton period={period} setPeriod={setPeriod} />
      <AccountFilterButton
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
      />
      {/* No network filter on lots: token options are already per-chain, so
          the token filter carries the network. */}
      {!isLots && (
        <NetworkFilterButton
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
        />
      )}
      {isLots && (
        <>
          <TokenFilterButton
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
          />
          <RemainingFilterButton
            value={lotsRemaining}
            onChange={onLotsRemainingChange}
          />
          <CostBasisFilterButton
            value={lotsCostBasis}
            onChange={onLotsCostBasisChange}
          />
          <UnitCostFilterButton
            value={lotsUnitCost}
            onChange={onLotsUnitCostChange}
          />
        </>
      )}
      {isTransactions && (
        <>
          <TokenFilterButton
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
          />
          <ValueFilterButton
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
          />
          <div className="ml-auto">
            <TransactionsExportButton filterParams={filterParams} />
          </div>
        </>
      )}
      {isLots && (
        <div className="ml-auto">
          <LotsExportButton filterParams={filterParams} />
        </div>
      )}
      {tab === 'tokens' && (
        <div className="ml-auto">
          <TokensExportButton
            filterParams={filterParams}
            search={tokenSearch}
          />
        </div>
      )}
    </div>
  )
}

// ─── Token Search ─────────────────────────────────────────────────────────────

const TokenSearch = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => (
  // Match the Transactions search width so switching tabs doesn't shift the page.
  <div className="w-64">
    <Input
      inputSize="small"
      placeholder="Search tokens..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      prefix={<SearchIcon className="h-3.5 w-3.5 text-secondary" />}
      onClearInput={value ? () => onChange('') : undefined}
    />
  </div>
)

// ─── Debounced Search ────────────────────────────────────────────────────────

const SEARCH_DEBOUNCE_MS = 300

const DebouncedSearch = ({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string
  value: string
  onChange: (value: string) => void
}) => {
  const [localValue, setLocalValue] = useState(value)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedOnChange = useCallback(
    (newValue: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        onChange(newValue)
      }, SEARCH_DEBOUNCE_MS)
    },
    [onChange],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return (
    <div className="w-64">
      <Input
        inputSize="small"
        placeholder={placeholder}
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value)
          debouncedOnChange(e.target.value)
        }}
        prefix={<SearchIcon className="h-3.5 w-3.5 text-secondary" />}
        onClearInput={
          localValue
            ? () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current)
                setLocalValue('')
                onChange('')
              }
            : undefined
        }
      />
    </div>
  )
}

const ExportCSVButton = ({
  eventName,
  isGenerating,
  generateCSV,
}: {
  eventName: string
  isGenerating: boolean
  generateCSV: () => void
}) => {
  const { org } = useActiveOrg()

  return (
    <Button
      variant="secondary"
      size="small"
      type="button"
      isLoading={isGenerating}
      isDisabled={isGenerating}
      disabledReason="Generating..."
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        generateCSV()
      }}
      eventName={eventName}
      eventProperties={{ orgId: org?.id }}
    >
      Export CSV
    </Button>
  )
}

const TransactionsExportButton = ({
  filterParams,
}: {
  filterParams: FilterParams
}) => {
  const { isGenerating, generateCSV } = useTransactionsExport(filterParams)

  return (
    <ExportCSVButton
      eventName="accounting-export-transactions-to-csv"
      isGenerating={isGenerating}
      generateCSV={generateCSV}
    />
  )
}

// Scoped by the shared account/token filters and the period end only; the lots
// report doesn't take the tab-only filters (sender search, remaining bounds,
// status) or the period start.
const LotsExportButton = ({ filterParams }: { filterParams: FilterParams }) => {
  const { isGenerating, generateCSV } = useOpenLotsExport(
    // No chainIds: the lots tab ignores the network filter.
    omit(filterParams, ['chainIds']),
  )

  return (
    <ExportCSVButton
      eventName="accounting-export-token-lots-to-csv"
      isGenerating={isGenerating}
      generateCSV={generateCSV}
    />
  )
}

const TokensExportButton = ({
  filterParams,
  search,
}: {
  filterParams: FilterParams
  search: string
}) => {
  const { isGenerating, generateCSV } = useTokensExport({
    filterParams,
    search,
  })

  return (
    <ExportCSVButton
      eventName="accounting-export-tokens-to-csv"
      isGenerating={isGenerating}
      generateCSV={generateCSV}
    />
  )
}
